import { dashboardAppointmentSchema } from './appointments-contracts'
import {
  normalizeAppointment,
  type AppointmentItem,
} from './appointments-model'

export type AppointmentWindow = {
  startDate: string
  endDate: string
  dates: string[]
  startAtMs: number
  endBeforeMs: number
  timeZone: string
}

const DAY_MS = 86_400_000
const MAX_WINDOW_DAYS = 42
const MAX_APPOINTMENTS_PER_DAY = 300

function parts(timestamp: number, timeZone: string) {
  const values = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(timestamp)
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    Number(values.find((item) => item.type === type)?.value)
  return {
    year: value('year'),
    month: value('month'),
    day: value('day'),
    hour: value('hour'),
    minute: value('minute'),
    second: value('second'),
  }
}

function midnight(date: string, timeZone: string) {
  const [year, month, day] = date.split('-').map(Number)
  if (!year || !month || !day) return null
  const target = Date.UTC(year, month - 1, day)
  let candidate = target
  for (let attempt = 0; attempt < 4; attempt += 1) {
    const rendered = parts(candidate, timeZone)
    candidate +=
      target -
      Date.UTC(
        rendered.year,
        rendered.month - 1,
        rendered.day,
        rendered.hour,
        rendered.minute,
        rendered.second
      )
  }
  const rendered = parts(candidate, timeZone)
  return rendered.year === year &&
    rendered.month === month &&
    rendered.day === day &&
    rendered.hour === 0 &&
    rendered.minute === 0
    ? candidate
    : null
}

function addDays(date: string, count: number) {
  return new Date(Date.parse(`${date}T00:00:00Z`) + count * DAY_MS)
    .toISOString()
    .slice(0, 10)
}

export function buildAppointmentWindow(
  dates: string[],
  timeZone: string
): AppointmentWindow | null {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone }).format(0)
  } catch {
    return null
  }
  if (!dates.length || dates.length > MAX_WINDOW_DAYS) return null
  const sorted = [...new Set(dates)].sort()
  if (
    sorted.length !== dates.length ||
    sorted.some((date, index) =>
      index === 0 ? false : date !== addDays(sorted[0]!, index)
    )
  ) {
    return null
  }
  const startDate = sorted[0]!
  const endDate = addDays(sorted.at(-1)!, 1)
  const startAtMs = midnight(startDate, timeZone)
  const endBeforeMs = midnight(endDate, timeZone)
  if (startAtMs === null || endBeforeMs === null || endBeforeMs <= startAtMs) {
    return null
  }
  return {
    startDate,
    endDate,
    dates: sorted,
    startAtMs,
    endBeforeMs,
    timeZone,
  }
}

export function parseAppointmentWindow(
  value: unknown,
  window: AppointmentWindow
): { valid: boolean; items: AppointmentItem[] } {
  if (value === null) return { valid: true, items: [] }
  if (typeof value !== 'object' || Array.isArray(value)) {
    return { valid: false, items: [] }
  }
  const entries = Object.entries(value as Record<string, unknown>)
  if (entries.length > window.dates.length * MAX_APPOINTMENTS_PER_DAY) {
    return { valid: false, items: [] }
  }
  const items: AppointmentItem[] = []
  for (const [id, raw] of entries) {
    const parsed = dashboardAppointmentSchema.safeParse(raw)
    if (
      !parsed.success ||
      parsed.data.appointmentId !== id ||
      parsed.data.scheduledAtMs === null ||
      parsed.data.scheduledAtMs < window.startAtMs ||
      parsed.data.scheduledAtMs >= window.endBeforeMs
    ) {
      return { valid: false, items: [] }
    }
    items.push(normalizeAppointment(parsed.data))
  }
  items.sort(
    (left, right) =>
      (left.scheduledAtMs ?? 0) - (right.scheduledAtMs ?? 0) ||
      left.appointmentId.localeCompare(right.appointmentId)
  )
  return { valid: true, items }
}
